<template>
  <div
    v-if="isVisible"
    class="min-width-rinput flex-basis-rinput"
    :class="hasCol ? ['v-col min-width-rinput flex-basis-rinput py-0',
                      {
                        [`input-${containerSize}`]: containerSize,
                        'px-0': isDataTableField,
                        'double-flex-basis-rinput': localWidth === 2,
                        'triple-flex-basis-rinput': localWidth === 3,
                      }] : ''"
  >
    <component
      :is="localRComponent"
      :reRenderKey="localReRenderKey"
      :class="{
        'custom-icon-size': ['RAutocomplete', 'RSelect'].includes(rComponentName),
        'min-width-rinput': !hasCol && minWidth && rComponentName !== 'RCodeEditor',
        'px-0 max-width-rinput-datatable': hasCol && isDataTableField,
        'double-max-width-rinput': localWidth === 2,
        'triple-max-width-rinput': localWidth === 3,
        'double-flex-basis-rinput': !hasCol && localWidth === 2,
        'triple-flex-basis-rinput': !hasCol && localWidth === 3,
        'dismissable-messages': !fieldAuthInstance,
      }"
      :hasToolTipMessages="!!toolTipMessages.length"
      :name
      :model
      :label
      :value
      :hint
      :persistentHint
      :meta
      :classMeta
      :parentPath
      :propertyKey
      :isPersisted
      :fieldAuthInstance
      :fieldAuthProps
      :inputTypeOverride
      :enabledOverride
      :compositeKeyNeeded
      :width
      :requiredFields
      :items
      :comboIdKeys
      :clearable
      :singleLine
      :fullWidth
      :emitObject
      :hideDetails
      :disabled
      :rComponentName
      :allowedDates
      :allowTimeSelect
      :isSearchField
      :renderAsTextField
      :actionParameter
      :trueValue
      :falseValue
      :customRemoveFn
      autocomplete="new-password"
      @focus="e => handleFocusEvent('focus', e)"
      @blur="e => handleFocusEvent('blur', e)"
      @tab="e => emit('tab', e)"
      @enter="e => emit('enter', e)"
      @click="e => emit('click', e)"
      @update-value="e => emit('update-value', e)"
      @selected-item="e => emit('selected-item', e)"
      @toggle-menu="e => emit('toggle-menu', e)"
      @clear-messages="e => emit('clear-messages', e)"
      @update-field-auth="e => emit('update-field-auth', e)"
      @toggle-field-auth="e => emit('toggle-field-auth', e)"
      @combo-uql-override="e => emit('combo-uql-override', e)"
      @has-messages="e => emit('has-messages', e)"
    >
      <template #append-inner>
        <slot name="append-inner" />
      </template>
      <template #append>
        <slot name="append" />
        <ToolTip
          v-if="rComponentName !== 'RCheckbox' && toolTipMessages.length"
          :buttonClasses="['raised-tool-tip-checkbox']"
          :messages="toolTipMessages"
          :iconOptionsOverrides="getIconColor"
          :justifySpaceBetween="justify"
        />
      </template>
      <template #label>
        <template v-if="rComponentName === 'RCheckbox' && toolTipMessages.length">
          <slot name="label">
            <span>{{ localLabel }}</span>
            <ToolTip
              :buttonClasses="['raised-tool-tip']"
              :messages="toolTipMessages"
              :iconOptionsOverrides="getIconColor"
              :justifySpaceBetween="justify"
            />
          </slot>
        </template>
      </template>
    </component>
  </div>
</template>

<script setup>
import { defineAsyncComponent, computed, ref } from 'vue';
import { useStore } from 'vuex';
import _get from 'lodash/get';
import { ruid } from '@/lib/utils';
import { evaluateVisibleOnlyIf } from '@/lib/metaUtils';
import { baseRInputEmits, baseRInputProps, useBaseRInput } from '@/hooks/common/rinput/useBaseRInput';

/* istanbul ignore next */
const ToolTip = defineAsyncComponent(() => import(/* webpackChunkName: "ToolTip" */ '@/components/core/common/ToolTip.vue'));
/* istanbul ignore next */
const RTextField = defineAsyncComponent(() => import(/* webpackChunkName: "RTextField" */ '@/components/core/common/RTextField'));
/* istanbul ignore next */
const RTextArea = defineAsyncComponent(() => import(/* webpackChunkName: "RTextArea" */ '@/components/core/common/RTextArea'));
/* istanbul ignore next */
const RCheckbox = defineAsyncComponent(() => import(/* webpackChunkName: "RCheckbox" */ '@/components/core/common/RCheckbox'));
/* istanbul ignore next */
const RSelect = defineAsyncComponent(() => import(/* webpackChunkName: "RSelect" */ '@/components/core/common/RSelect'));
/* istanbul ignore next */
const RAutocomplete = defineAsyncComponent(() => import(/* webpackChunkName: "RAutocomplete" */ '@/components/core/common/RAutocomplete'));
/* istanbul ignore next */
const RCombobox = defineAsyncComponent(() => import(/* webpackChunkName: "RCombobox" */ '@/components/core/common/RCombobox'));
/* istanbul ignore next */
const RDatePicker = defineAsyncComponent(() => import(/* webpackChunkName: "RDatePicker" */ '@/components/core/common/RDatePicker'));
/* istanbul ignore next */
const RTimePicker = defineAsyncComponent(() => import(/* webpackChunkName: "RTimePicker" */ '@/components/core/common/RTimePicker'));
/* istanbul ignore next */
const RDeliveryCalendarInput = defineAsyncComponent(() => import(/* webpackChunkName: "RDeliveryCalendarInput" */ '@/components/core/common/RDeliveryCalendarInput'));
/* istanbul ignore next */
const RCodeEditor = defineAsyncComponent(() => import(/* webpackChunkName: "RCodeEditor" */ '@/components/core/common/RCodeEditor'));

/**
 * Dynamically import various RA input components into a generic RInput component.
 * Acts as a passthrough to render specified RInput type specified by rComponentName.
 * @vue-prop {String} name - Name attribute of the RInput field. Typically mirrors key within model.
 * @vue-prop {Object} model - Object containing all data pertinent to record.
 * @vue-prop {Object} meta - Individual property's meta data associated with a class's meta.properties.
 * @vue-prop {String} rComponentName - Explicit name of a dynamically import RInput component.
 * @vue-prop {} label - Label to be used in place of translation.
 * @vue-prop {String} value - Value to be used in place of corresponding model value.
 * @vue-prop {String} parentPath - Object dot notation string indicating current
 * location within deeply nested object.
 * @vue-prop {String} hint - String to be displayed as a hint underneath an RInput component.
 * @vue-prop {Boolean} fieldAuthInstance - Recursion base case for overrides. If an RInput's
 * underlying meta data requires a field authorization, then this prevents additional field
 * authorization appended icons from being added to RInput component.
 * @vue-prop {Boolean} isPersisted - Indicates that the passed model has been persisted to the database.
 * @vue-prop {Array} requiredFields - Array of required field strings. If the passed name
 * is defined within the list, then it will be marked with a red asterisk to right of label.
 * @vue-prop {Array} items - Array of items to be used for RSelect and RAutocomplete
 * in lieu default behavior.
 * @vue-prop {Boolean} [singleLine = false] - Prevents label from moving on focus/dirty. Please note,
 * this does not apply to RCheckboxes (use a label with an empty string instead).
 * Useful in cases, like editable data tables, where label are already already present.
 * @vue-prop {Object} [popoverData = null] - Contains messages and or messagesFunction keys, both should return arrays
 * @vue-prop {Object} [iconOptionsOverrides = null] - Contains custom colors for tooltip icon.  Should be an object { color: '<color>' }.
 * @vue-prop {Boolean} [renderAsTextField = false] - Requires rComponentName prop to equal 'RAutocomplete'.
 * If true, will render as RTextField when not focused.
 * @vue-event {Object} updateValue - Update object to emit to parent consisting key and a value
 * property where key is key to update and value is update value entered into corresponding input.
 * vue-event {Object} has-messages - Allows parent to know what fields are in an error or warning state.
 * vue-event {Object} clear-messages - Allows parent to know what fields are no longer in an error or warning state.
 */

const emit = defineEmits([...baseRInputEmits, 'combo-uql-override', 'enter', 'click', 'focus', 'blur', 'tab', 'selected-item', 'toggle-menu']);
const props = defineProps({
  ...baseRInputProps,
  clearable: {
    type: Boolean,
    default: false,
  },
  containerSize: {
    type: String,
    default: null,
  },
  falseValue: {
    type: [Boolean, String],
    default: 'N',
  },
  hasCol: {
    type: Boolean,
    default: true,
  },
  iconOptionsOverrides: {
    type: Object,
    default: null,
  },
  inputTypeOverride: {
    type: String,
    default: null,
  },
  minWidth: {
    type: Boolean,
    default: true,
  },
  parentPath: {
    type: String,
    default: '',
  },
  popoverData: {
    type: Object,
    default: null,
  },
  propertyKey: {
    type: String,
    default: 'deliverydate',
  },
  rComponentName: {
    type: String,
    required: true,
  },
  renderAsTextField: {
    type: Boolean,
    default: false,
  },
  trueValue: {
    type: [Boolean, String],
    default: 'Y',
  },
  value: {
    type: null,
    default: undefined,
  },
  allowedDates: {
    type: [Function, Array],
    default: null,
  },
  allowTimeSelect: {
    type: Boolean,
    default: true,
  },
  isSearchField: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();

const { localLabel } = useBaseRInput(props, emit);

const {
  hasFocus,
  localWidth,
  localRComponent,
  localReRenderKey,
} = useInputState();

const {
  justify,
  getIconColor,
  toolTipMessages,
} = useToolTips();

const {
  isVisible,
} = useIsVisible();

const {
  handleFocusEvent,
} = useEmits();


function useInputState() {
  const hasFocus = ref(false);

  const selectedItem = ref(null);

  const localWidth = computed(() => props.width || _get(props.meta, 'property_meta.textwidth', 0));

  const localRComponent = computed(() => {
    if (props.renderAsTextField && props.rComponentName === 'RAutocomplete') {
      return hasFocus.value ? RAutocomplete : RTextField;
    }

    return ({
      RTextField,
      RTextArea,
      RCheckbox,
      RSelect,
      RAutocomplete,
      RCombobox,
      RDatePicker,
      RTimePicker,
      RDeliveryCalendarInput,
      RCodeEditor,
    })[props.rComponentName];
  });

  const localReRenderKey = computed(() => {
    return props.reRenderKey || `rinput-${ruid()}`;
  });

  const handleSelectedItem = (event) => {
    if (props.renderAsTextField && props.name === 'RAutocomplete') {
      selectedItem.value = event.value;
    }
  };

  return {
    hasFocus,
    localWidth,
    localRComponent,
    localReRenderKey,
    handleSelectedItem,
  };
}

function useToolTips() {
  const justify = computed(() => !!_get(props.popoverData, 'justifySpaceBetween'));

  const getIconColor = computed(() => {
    const iconFunction = _get(props.iconOptionsOverrides, 'getIconColor');
    return iconFunction ? iconFunction(props.model) : { ...props.iconOptionsOverrides };
  });

  const toolTips = (() => {
    const label = `{${_get(props.classSchema, 'classname') || _get(props.model, '_classname')}.ToolTip.${props.name}}l`;
    const toolTip = store.getters['translations/label'](label);
    return toolTip && !label.startsWith(`{${toolTip}}`) ? toolTip.split('\\n') : [];
  })();

  /**
   * A flat array from popoverData for iterating over a v-list
   */
  const toolTipMessages = computed(() => {
    return [
      ...toolTips,
      ..._get(props.popoverData, 'messages', []),
      ..._get(props.popoverData, 'messagesFunction', () => [])(props.model),
    ];
  });

  return {
    justify,
    getIconColor,
    toolTipMessages,
  };
}

function useIsVisible() {
  /**
   * Determines if a RInput should render based on its 'visibleonlyif' condition
   * @param {String} visibleOnlyIf - A string containing conditions for the RInput to be rendered
   */
  const isVisible = computed(() => {
    const visibleOnlyIf = _get(props.meta, 'property_meta.visibleonlyif');
    if (!visibleOnlyIf || props.enabledOverride) return true;
    return evaluateVisibleOnlyIf(visibleOnlyIf, {
      model: props.model,
      record: store.getters['editObject/record'],
      parentPath: props.parentPath.replace('record.', ''),
      classMeta: props.classMeta,
    });
  });

  return {
    isVisible,
  };
}

function useEmits() {
  const handleFocusEvent = (type, event) => {
    if (props.renderAsTextField && props.rComponentName === 'RAutocomplete') {
      if (type === 'focus' && !hasFocus.value) hasFocus.value = true;
      else if (type === 'blur' && event.relatedTarget) hasFocus.value = false;
    }
    emit(type, event);
  };

  return {
    handleFocusEvent,
  };
}
</script>

<style lang="scss">
  .dismissable-messages {
    .v-messages__message:hover {
      cursor: pointer;
      text-decoration: underline;
    }

    .v-messages__message:last-child {
      // We need this to see the underline for the bottom message
      padding-bottom: 1px;
    }
  }

  .raised-tool-tip, .raised-tool-tip-checkbox {
    margin-left: 0;
    pointer-events: auto;
  }

  .raised-tool-tip {
    margin-top: -23px;
  }

  .raised-tool-tip-checkbox {
    margin-top: -16px;
  }

  .v-application--is-ltr {
    .v-input__append-outer {
      margin-left: 4px;
    }

    .v-input__prepend-outer {
      margin-right: 4px;
    }
  }

 .v-text-field.v-input--dense:not(.v-text-field--enclosed):not(.v-text-field--full-width) .v-input__append-inner .v-input__icon > .v-icon {
    margin-top: 0;
  }
</style>
